import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type { TFunction } from 'i18next'
import type { DiscoverRouteListItem } from '@xingroute/schema'
import { api } from '../api/client.js'
import { useAuth } from '../auth/AuthContext.js'
import { EmptyState } from '../components/ui/EmptyState.js'
import { PageToolbar } from '../components/ui/PageToolbar.js'
import { Button } from '../components/ui/Button.js'
import { FilterSheet } from '../components/FilterSheet.js'
import { DiscoverMapView } from '../components/DiscoverMapView.js'
import { RegionFilterPopover } from '../components/RegionFilterPopover.js'
import { AddRouteFab } from '../components/AddRouteFab.js'
import { usePopoverAnchor } from '../components/usePopoverAnchor.js'
import { isSeasonalitySuitableForDate } from '../components/RouteSeasonalityBlock.js'
import { RouteCard } from '../components/RouteCard.js'
import {
  CalendarIcon,
  ChevronDownIcon,
  ListIcon,
  MapIcon,
  SortIcon,
} from '../components/icons/GlyphIcons.js'
import { enumDisplay, type EnumField } from '../lib/enumDisplay.js'
import {
  useDiscoverFilters,
  type ActivityFilter,
  type DifficultyFilter,
  type RouteTypeFilter,
  type SortMode,
  type ViewMode,
} from '../hooks/useDiscoverFilters.js'
import { friendlyError } from '../lib/friendlyError.js'
import { SELECTABLE_ACTIVITIES } from '../lib/selectableActivities.js'

// DiscoverPage — the community route library, mounted at `/discover`. Per
// ADR 06 routes are community-edited (no owner); this page is the canonical
// way to browse the catalogue. A sticky filter row sits directly under the
// top chrome and drives client-side filtering / sorting; the actual server
// query stays simple (listRoutes()) until per-filter backend hooks land.

const DISCOVER_PAGE_SIZE = 24

export function DiscoverPage() {
  const auth = useAuth()
  const { t } = useTranslation('common')
  const [routes, setRoutes] = useState<DiscoverRouteListItem[] | null>(null)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(() => new Set())
  const [toast, setToast] = useState<string | null>(null)
  const [suitableThisMonth, setSuitableThisMonth] = useState(false)
  const suitableDate = useMemo(() => new Date(), [])
  const {
    activity,
    setActivity,
    sort,
    setSort,
    view,
    setView,
    count,
    setCount,
    region,
    setRegion,
    routeType,
    setRouteType,
    difficulty,
    setDifficulty,
    bbox,
    setBbox,
  } = useDiscoverFilters()
  const routeQueryKey = JSON.stringify([activity, difficulty, region, routeType, sort])
  const activeRouteQueryKeyRef = useRef(routeQueryKey)
  const routeQueryGenerationRef = useRef(0)
  useLayoutEffect(() => {
    activeRouteQueryKeyRef.current = routeQueryKey
  }, [routeQueryKey])

  useEffect(() => {
    let cancelled = false
    const generation = ++routeQueryGenerationRef.current
    setRoutes(null)
    setNextCursor(null)
    setLoadingMore(false)
    setError(null)
    void api
      .listRoutePage({
        status: 'published',
        limit: DISCOVER_PAGE_SIZE,
        includeSeasonality: true,
        includeThumbnail: true,
        activity: activity === 'all' ? undefined : activity,
        difficulty: difficulty === 'all' ? undefined : difficulty,
        routeType: routeType === 'all' ? undefined : routeType,
        region: region || undefined,
        sort,
      })
      .then(
        (page) => {
          if (
            cancelled ||
            generation !== routeQueryGenerationRef.current ||
            routeQueryKey !== activeRouteQueryKeyRef.current
          )
            return
          setRoutes(page.items)
          setNextCursor(page.next_cursor ?? null)
        },
        (err) => {
          if (
            cancelled ||
            generation !== routeQueryGenerationRef.current ||
            routeQueryKey !== activeRouteQueryKeyRef.current
          )
            return
          setRoutes([])
          setError(friendlyError(err, t))
        },
      )
    return () => {
      cancelled = true
      if (routeQueryGenerationRef.current === generation) {
        routeQueryGenerationRef.current += 1
      }
    }
  }, [activity, difficulty, region, routeQueryKey, routeType, sort, t])

  async function loadMoreRoutes() {
    if (!nextCursor || loadingMore) return
    const requestQueryKey = routeQueryKey
    const requestGeneration = routeQueryGenerationRef.current
    setLoadingMore(true)
    setError(null)
    try {
      const page = await api.listRoutePage({
        status: 'published',
        limit: DISCOVER_PAGE_SIZE,
        cursor: nextCursor,
        includeSeasonality: true,
        includeThumbnail: true,
        activity: activity === 'all' ? undefined : activity,
        difficulty: difficulty === 'all' ? undefined : difficulty,
        routeType: routeType === 'all' ? undefined : routeType,
        region: region || undefined,
        sort,
      })
      if (
        requestQueryKey !== activeRouteQueryKeyRef.current ||
        requestGeneration !== routeQueryGenerationRef.current
      )
        return
      setRoutes((current) => [...(current ?? []), ...page.items])
      setNextCursor(page.next_cursor ?? null)
    } catch (err) {
      if (
        requestQueryKey !== activeRouteQueryKeyRef.current ||
        requestGeneration !== routeQueryGenerationRef.current
      )
        return
      setError(friendlyError(err, t))
    } finally {
      if (
        requestQueryKey === activeRouteQueryKeyRef.current &&
        requestGeneration === routeQueryGenerationRef.current
      ) {
        setLoadingMore(false)
      }
    }
  }

  useEffect(() => {
    if (auth.status !== 'authed' || !auth.token) {
      setFavoriteIds(new Set())
      return
    }

    let cancelled = false
    api
      .listFavorites(auth.token)
      .then((items) => {
        if (!cancelled) setFavoriteIds(new Set(items.map((item) => item.route_id)))
      })
      .catch(() => {
        if (!cancelled) setFavoriteIds(new Set())
      })
    return () => {
      cancelled = true
    }
  }, [auth.status, auth.token])

  const filtered = useMemo(() => {
    if (!routes) return null
    let r = routes.filter((x) => x.status === 'published')
    if (activity !== 'all') {
      r = r.filter((x) => x.activities.includes(activity))
    }
    if (routeType !== 'all') r = r.filter((x) => x.route_type === routeType)
    if (difficulty !== 'all') r = r.filter((x) => x.difficulty === difficulty)
    if (suitableThisMonth) {
      r = r.filter((x) => isSeasonalitySuitableForDate(x.seasonality, suitableDate))
    }
    if (region) r = r.filter((x) => routeMatchesRegion(x, region))
    const sorted = [...r]
    if (sort === 'distance') {
      sorted.sort((a, b) => (b.distance_km ?? 0) - (a.distance_km ?? 0))
    } else if (sort === 'ascent') {
      sorted.sort((a, b) => (b.ascent_m ?? 0) - (a.ascent_m ?? 0))
    }
    return sorted
  }, [routes, activity, routeType, difficulty, region, suitableDate, suitableThisMonth, sort])

  useEffect(() => {
    if (!toast) return
    const timer = window.setTimeout(() => setToast(null), 2200)
    return () => window.clearTimeout(timer)
  }, [toast])

  useEffect(() => {
    if (view === 'list') setCount(filtered?.length ?? 0)
  }, [filtered?.length, setCount, view])

  return (
    <div className="flex flex-col lg:h-full lg:min-h-0 lg:overflow-hidden">
      <FilterRow
        activity={activity}
        onActivity={setActivity}
        sort={sort}
        onSort={setSort}
        suitableThisMonth={suitableThisMonth}
        onSuitableThisMonth={setSuitableThisMonth}
        view={view}
        onView={setView}
        region={region}
        onRegion={setRegion}
        routeType={routeType}
        onRouteType={setRouteType}
        difficulty={difficulty}
        onDifficulty={setDifficulty}
      />

      <div
        className={
          view === 'map'
            ? 'flex-1 lg:min-h-0 lg:overflow-y-auto'
            : 'px-5 py-5 pb-24 sm:pb-5 lg:min-h-0 lg:flex-1 lg:overflow-y-auto'
        }
      >
        {error && (
          <p className="mb-4 text-sm text-[var(--xr-danger)]" data-testid="routes-error">
            {error}
          </p>
        )}
        {view === 'map' ? (
          <DiscoverMapView
            activity={activity}
            status={'published'}
            sort={sort}
            suitableThisMonth={suitableThisMonth}
            region={region}
            routeType={routeType}
            difficulty={difficulty}
            bbox={bbox}
            onBboxChange={setBbox}
            onCountChange={setCount}
          />
        ) : !filtered ? (
          <p className="text-sm text-[var(--xr-text-muted)]">…</p>
        ) : filtered.length === 0 ? (
          <EmptyState
            variant="not-found"
            title={t('listEmpty')}
            description={t('listEmptyDesc')}
            action={
              nextCursor ? (
                <Button
                  type="button"
                  variant="secondary"
                  disabled={loadingMore}
                  onClick={() => void loadMoreRoutes()}
                >
                  {loadingMore ? t('discover.loadingMore') : t('discover.loadMore')}
                </Button>
              ) : auth.status === 'authed' ? (
                <Link to="/routes/new">
                  <Button>{t('createFirst')}</Button>
                </Link>
              ) : (
                <Link to="/signin">
                  <Button variant="secondary">{t('signInToStart')}</Button>
                </Link>
              )
            }
          />
        ) : (
          <div className="space-y-5">
            <ul
              data-testid="routes-list"
              className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            >
              {filtered.map((r) => (
                <RouteCard
                  key={r.id}
                  route={r}
                  favoriteInitial={favoriteIds.has(r.id)}
                  showStatusChip={false}
                  interactive={auth.status === 'authed'}
                />
              ))}
            </ul>
            {nextCursor && (
              <div className="flex justify-center">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={loadingMore}
                  onClick={() => void loadMoreRoutes()}
                  data-testid="discover-load-more"
                >
                  {loadingMore ? t('discover.loadingMore') : t('discover.loadMore')}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      {toast && (
        <div
          role="status"
          className="fixed right-5 top-20 z-50 rounded-[var(--xr-radius-md)] border border-[var(--xr-accent)]/30 bg-[var(--xr-accent-soft)] px-4 py-2 text-sm font-medium text-[var(--xr-accent)] shadow-[var(--xr-shadow-md)]"
        >
          {toast}
        </div>
      )}
      {auth.status === 'authed' && <AddRouteFab />}
    </div>
  )
}

interface FilterRowProps {
  activity: ActivityFilter
  onActivity: (next: ActivityFilter) => void
  sort: SortMode
  onSort: (next: SortMode) => void
  suitableThisMonth: boolean
  onSuitableThisMonth: (next: boolean) => void
  view: ViewMode
  onView: (next: ViewMode) => void
  region: string
  onRegion: (next: string) => void
  routeType: RouteTypeFilter
  onRouteType: (next: RouteTypeFilter) => void
  difficulty: DifficultyFilter
  onDifficulty: (next: DifficultyFilter) => void
}

function FilterRow({
  activity,
  onActivity,
  sort,
  onSort,
  suitableThisMonth,
  onSuitableThisMonth,
  view,
  onView,
  region,
  onRegion,
  routeType,
  onRouteType,
  difficulty,
  onDifficulty,
}: FilterRowProps) {
  const { t } = useTranslation('common')
  const [filterSheetOpen, setFilterSheetOpen] = useState(false)
  const tFn = t as TFunction
  const moreFilterCount =
    (activity !== 'all' ? 1 : 0) + (routeType !== 'all' ? 1 : 0) + (difficulty !== 'all' ? 1 : 0)
  const activeFilters = buildActiveDrawerFilters({
    activity,
    onActivity,
    routeType,
    onRouteType,
    difficulty,
    onDifficulty,
    t: tFn,
  })

  return (
    <PageToolbar testId="discover-filter-row">
      <div className="flex min-w-0 items-center gap-2 sm:gap-3" data-testid="discover-toolbar">
        <ViewModeDropdown value={view} onChange={onView} />

        <div
          className="relative -mr-3 min-w-0 flex-1 sm:mr-0"
          data-testid="discover-primary-filter-region"
        >
          <div
            className="flex snap-x snap-mandatory items-center gap-2 overflow-x-auto scroll-smooth overscroll-x-contain pr-3 [scrollbar-width:none] sm:pr-0"
            data-testid="discover-primary-filter-scroller"
          >
            <PrimaryFilterChips
              region={region}
              onRegion={onRegion}
              suitableThisMonth={suitableThisMonth}
              onSuitableThisMonth={onSuitableThisMonth}
              moreFilterCount={moreFilterCount}
              onOpenSheet={() => setFilterSheetOpen(true)}
              filterSheetOpen={filterSheetOpen}
              t={t}
            />
          </div>
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 sm:hidden"
            style={{
              backgroundImage: 'linear-gradient(to left, var(--xr-bg-surface), transparent)',
            }}
          />
        </div>

        <span className="flex-none border-l border-[var(--xr-border-default)] pl-2">
          <SortGhostButton sort={sort} onSort={onSort} />
        </span>
      </div>

      {activeFilters.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-1.5"
          data-testid="discover-active-drawer-filters"
        >
          {activeFilters.map((filter) => (
            <button
              key={filter.key}
              type="button"
              className="inline-flex h-7 items-center gap-1 rounded-full border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface-elev)] px-2.5 text-xs font-medium text-[var(--xr-text-secondary)] hover:border-[var(--xr-brand-primary)] hover:text-[var(--xr-brand-primary)]"
              onClick={filter.onRemove}
            >
              <span>{filter.label}</span>
              <span aria-hidden>×</span>
            </button>
          ))}
        </div>
      )}
      <FilterSheet
        open={filterSheetOpen}
        title={t('discover.drawerTitle')}
        onClose={() => setFilterSheetOpen(false)}
        closeLabel={t('cancel')}
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                onActivity('all')
                onRouteType('all')
                onDifficulty('all')
              }}
            >
              {t('discover.clearAll')}
            </Button>
            <Button type="button" onClick={() => setFilterSheetOpen(false)}>
              {t('apply')}
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          <DrawerFilterSection label={t('filterActivity')}>
            {SELECTABLE_ACTIVITIES.map((option) => (
              <EnumChip
                key={option}
                field="activity"
                value={option}
                active={activity === option}
                onClick={() => onActivity(activity === option ? 'all' : option)}
                t={tFn}
              />
            ))}
          </DrawerFilterSection>
          <DrawerFilterSection label={t('filterDifficulty')}>
            {(['easy', 'moderate', 'hard', 'expert'] as const).map((d) => (
              <EnumChip
                key={d}
                field="difficulty"
                value={d}
                active={difficulty === d}
                onClick={() => onDifficulty(difficulty === d ? 'all' : d)}
                t={tFn}
              />
            ))}
          </DrawerFilterSection>
          <DrawerFilterSection label={t('filterRouteType')}>
            {(['loop', 'point_to_point', 'out_and_back'] as const).map((rt) => (
              <EnumChip
                key={rt}
                field="route_type"
                value={rt}
                active={routeType === rt}
                onClick={() => onRouteType(routeType === rt ? 'all' : rt)}
                t={tFn}
              />
            ))}
          </DrawerFilterSection>
        </div>
      </FilterSheet>
    </PageToolbar>
  )
}

interface ActiveDrawerFilter {
  key: string
  label: string
  onRemove: () => void
}

function buildActiveDrawerFilters({
  activity,
  onActivity,
  routeType,
  onRouteType,
  difficulty,
  onDifficulty,
  t,
}: {
  activity: ActivityFilter
  onActivity: (next: ActivityFilter) => void
  routeType: RouteTypeFilter
  onRouteType: (next: RouteTypeFilter) => void
  difficulty: DifficultyFilter
  onDifficulty: (next: DifficultyFilter) => void
  t: TFunction
}): ActiveDrawerFilter[] {
  return [
    ...(activity !== 'all'
      ? [
          {
            key: 'activity:' + activity,
            label: enumDisplay('activity', activity, t).label,
            onRemove: () => onActivity('all'),
          },
        ]
      : []),
    ...(routeType !== 'all'
      ? [
          {
            key: 'route_type:' + routeType,
            label: enumDisplay('route_type', routeType, t).label,
            onRemove: () => onRouteType('all'),
          },
        ]
      : []),
    ...(difficulty !== 'all'
      ? [
          {
            key: 'difficulty:' + difficulty,
            label: enumDisplay('difficulty', difficulty, t).label,
            onRemove: () => onDifficulty('all'),
          },
        ]
      : []),
  ]
}

function DrawerFilterSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--xr-text-muted)]">
        {label}
      </h3>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-2">{children}</div>
    </section>
  )
}

function routeMatchesRegion(route: DiscoverRouteListItem, region: string): boolean {
  const wanted = region.trim().toLocaleLowerCase()
  if (!wanted) return true
  const values = route.region ? Object.values(route.region) : []
  return values.some((value) => value?.trim().toLocaleLowerCase() === wanted)
}

// PrimaryFilterChips — the filter pill chips (Region popover, Suitable-this-month
// toggle, More-filters drawer opener). Activity now lives in the filter drawer.
function PrimaryFilterChips({
  region,
  onRegion,
  suitableThisMonth,
  onSuitableThisMonth,
  moreFilterCount,
  onOpenSheet,
  filterSheetOpen,
  t,
}: {
  region: string
  onRegion: (next: string) => void
  suitableThisMonth: boolean
  onSuitableThisMonth: (next: boolean) => void
  moreFilterCount: number
  onOpenSheet: () => void
  filterSheetOpen: boolean
  t: TFunction
}) {
  return (
    <div className="inline-flex flex-none items-center gap-2">
      <RegionFilterPopover value={region} onChange={onRegion} />
      <Chip
        active={suitableThisMonth}
        onClick={() => onSuitableThisMonth(!suitableThisMonth)}
        label={t('discover.suitableThisMonth')}
        icon={<CalendarIcon />}
      />
      <button
        type="button"
        className="inline-flex h-7 flex-none shrink-0 snap-start items-center gap-1 whitespace-nowrap rounded-full border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] px-3 text-xs font-medium text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)]"
        onClick={onOpenSheet}
        aria-haspopup="dialog"
        aria-expanded={filterSheetOpen}
      >
        <span>{t('discover.moreFilters')}</span>
        {moreFilterCount > 0 && (
          <span
            className="inline-flex min-w-5 justify-center rounded-full bg-[var(--xr-brand-primary)] px-1.5 py-0.5 text-[10px] font-bold leading-none text-[var(--xr-brand-primary-ink)]"
            aria-label={t('discover.moreFiltersActiveCount', { count: moreFilterCount })}
          >
            {moreFilterCount}
          </span>
        )}
      </button>
    </div>
  )
}

function EnumChip({
  field,
  value,
  active,
  onClick,
  t,
}: {
  field: EnumField
  value: string
  active: boolean
  onClick: () => void
  t: TFunction
}) {
  const display = enumDisplay(field, value, t)
  return (
    <Chip
      active={active}
      onClick={onClick}
      label={display.label}
      title={display.tooltip}
      colour={display.colour}
    />
  )
}

function Chip({
  active,
  onClick,
  label,
  title,
  colour,
  icon,
}: {
  active: boolean
  onClick: () => void
  label: string
  title?: string
  colour?: string
  icon?: ReactNode
}) {
  const cls = active
    ? 'inline-flex h-7 flex-none shrink-0 snap-start items-center gap-1 whitespace-nowrap rounded-full border border-[var(--xr-brand-primary)] bg-[var(--xr-brand-primary-soft)] px-3 text-xs font-medium text-[var(--xr-brand-primary)]'
    : 'inline-flex h-7 flex-none shrink-0 snap-start items-center gap-1 whitespace-nowrap rounded-full border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] px-3 text-xs font-medium text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)]'
  const style = colour
    ? {
        borderColor: colour,
        backgroundColor: active ? colour : `${colour}24`,
        color: active ? 'var(--xr-brand-primary-ink)' : 'var(--xr-text-primary)',
      }
    : undefined
  return (
    <button type="button" onClick={onClick} className={cls} title={title} style={style}>
      {icon ? <span className="inline-flex flex-none">{icon}</span> : null}
      <span>{label}</span>
    </button>
  )
}

function ViewModeDropdown({
  value,
  onChange,
}: {
  value: ViewMode
  onChange: (next: ViewMode) => void
}) {
  const { t } = useTranslation('common')
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const handlePointer = (event: MouseEvent) => {
      const target = event.target as Node
      if (popoverRef.current?.contains(target) || buttonRef.current?.contains(target)) return
      setOpen(false)
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('mousedown', handlePointer)
    window.addEventListener('keydown', handleKey)
    return () => {
      window.removeEventListener('mousedown', handlePointer)
      window.removeEventListener('keydown', handleKey)
    }
  }, [open])

  const options: Array<{ value: ViewMode; label: string; icon: ReactNode }> = [
    { value: 'list', label: t('discover.viewList'), icon: <ListIcon /> },
    { value: 'map', label: t('discover.viewMap'), icon: <MapIcon /> },
  ]
  const current = options.find((option) => option.value === value) ?? options[0]!
  const anchor = usePopoverAnchor(buttonRef, open, { align: 'left', gap: 4 })

  return (
    <div className="relative inline-flex flex-none">
      <button
        ref={buttonRef}
        type="button"
        className="inline-flex h-7 flex-none items-center gap-1.5 whitespace-nowrap rounded-full border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] px-3 text-xs font-medium text-[var(--xr-text-secondary)] transition hover:bg-[var(--xr-bg-surface-elev)]"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="discover-view-dropdown"
        aria-label={`${t('discover.viewAriaLabel')}: ${current.label}`}
      >
        <span className="inline-flex flex-none">{current.icon}</span>
        <span>{current.label}</span>
        <ChevronDownIcon width={10} height={10} />
      </button>
      {open &&
        anchor &&
        createPortal(
          <div
            ref={popoverRef}
            role="listbox"
            aria-label={t('discover.viewAriaLabel')}
            style={{ position: 'fixed', top: anchor.top, left: anchor.left }}
            className="z-30 w-40 max-w-[calc(100vw-2rem)] overflow-hidden rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] py-1 shadow-[var(--xr-shadow-md)]"
            data-testid="discover-view-popover"
          >
            {options.map((option) => {
              const selected = option.value === value
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  data-testid={`discover-view-option-${option.value}`}
                  className={
                    'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs ' +
                    (selected
                      ? 'bg-[var(--xr-brand-primary-soft)] font-semibold text-[var(--xr-brand-primary)]'
                      : 'text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)]')
                  }
                  onClick={() => {
                    onChange(option.value)
                    setOpen(false)
                  }}
                >
                  <span className="inline-flex flex-none">{option.icon}</span>
                  <span className="flex-1">{option.label}</span>
                  {selected && <span aria-hidden>✓</span>}
                </button>
              )
            })}
          </div>,
          document.body,
        )}
    </div>
  )
}

// SortGhostButton — visually-distinct from filter chips: no border, muted
// "排序:" prefix, brand colour only when sort != recent. This signals that
// sort is a *view-order* control, not a filter narrowing the result set
// (see wiki/decisions/06-design-system.md §Discover toolbar).
function SortGhostButton({ sort, onSort }: { sort: SortMode; onSort: (next: SortMode) => void }) {
  const { t } = useTranslation('common')
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const handlePointer = (event: MouseEvent) => {
      const target = event.target as Node
      if (popoverRef.current?.contains(target) || buttonRef.current?.contains(target)) return
      setOpen(false)
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('mousedown', handlePointer)
    window.addEventListener('keydown', handleKey)
    return () => {
      window.removeEventListener('mousedown', handlePointer)
      window.removeEventListener('keydown', handleKey)
    }
  }, [open])

  const options: Array<{ value: SortMode; label: string }> = [
    { value: 'recent', label: t('sortRecent') },
    { value: 'distance', label: t('sortDistance') },
    { value: 'ascent', label: t('sortAscent') },
  ]
  const current = options.find((option) => option.value === sort) ?? options[0]!
  const active = sort !== 'recent'
  const anchor = usePopoverAnchor(buttonRef, open, { align: 'right', gap: 4 })
  const cls = active
    ? 'inline-flex h-7 flex-none shrink-0 items-center gap-1 whitespace-nowrap bg-transparent px-2 text-xs font-semibold text-[var(--xr-brand-primary)]'
    : 'inline-flex h-7 flex-none shrink-0 items-center gap-1 whitespace-nowrap bg-transparent px-2 text-xs font-medium text-[var(--xr-text-muted)] hover:text-[var(--xr-text-primary)]'
  return (
    <div className="relative inline-flex">
      <button
        ref={buttonRef}
        type="button"
        className={cls}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="discover-sort-chip"
        aria-label={`${t('filterSort')}: ${current.label}`}
      >
        <SortIcon />
        <span className="text-[var(--xr-text-muted)] font-medium">{t('filterSort')}</span>
        <span>{current.label}</span>
        <ChevronDownIcon width={10} height={10} />
      </button>
      {open &&
        anchor &&
        createPortal(
          <div
            ref={popoverRef}
            role="listbox"
            aria-label={t('filterSort')}
            style={{
              position: 'fixed',
              top: anchor.top,
              left: Math.max(8, anchor.left - 176),
            }}
            className="z-30 w-44 max-w-[calc(100vw-2rem)] overflow-hidden rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] py-1 shadow-[var(--xr-shadow-md)]"
            data-testid="discover-sort-popover"
          >
            {options.map((option) => {
              const selected = option.value === sort
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={
                    'flex w-full items-center justify-between px-3 py-1.5 text-left text-xs ' +
                    (selected
                      ? 'bg-[var(--xr-brand-primary-soft)] font-semibold text-[var(--xr-brand-primary)]'
                      : 'text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)]')
                  }
                  onClick={() => {
                    onSort(option.value)
                    setOpen(false)
                  }}
                >
                  <span>{option.label}</span>
                  {selected && <span aria-hidden>✓</span>}
                </button>
              )
            })}
          </div>,
          document.body,
        )}
    </div>
  )
}
